/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { apiClient, ApiError } from '../api/apiClient';

// Types
interface User {
  id: string;
  email: string;
  name: string;
  role: 'CUSTOMER' | 'THERAPIST' | 'ADMIN' | 'SUPER_ADMIN';
  permissions: string[]; // Server-provided permissions
}

// Permission type (matches backend AdminPermissionService)
export type Permission =
  | 'VIEW_DASHBOARD'
  | 'MANAGE_BOOKINGS'
  | 'MANAGE_SERVICES'
  | 'MANAGE_THERAPISTS'
  | 'MANAGE_USERS'
  | 'VIEW_REPORTS'
  | 'MANAGE_SETTINGS'
  | 'PROCESS_REFUNDS'
  | 'VIEW_AUDIT_LOGS'
  | 'MANAGE_PROMOTIONS';

// Fallback permission matrix for backward compatibility
// Will be removed once all users have fresh login with permissions
const FALLBACK_PERMISSION_MATRIX: Record<string, Permission[]> = {
  CUSTOMER: [],
  THERAPIST: [],
  ADMIN: ['VIEW_DASHBOARD', 'MANAGE_BOOKINGS', 'MANAGE_SERVICES', 'MANAGE_THERAPISTS', 'MANAGE_USERS', 'VIEW_REPORTS', 'PROCESS_REFUNDS', 'VIEW_AUDIT_LOGS'],
  SUPER_ADMIN: [
    'VIEW_DASHBOARD',
    'MANAGE_BOOKINGS',
    'MANAGE_SERVICES',
    'MANAGE_THERAPISTS',
    'MANAGE_USERS',
    'VIEW_REPORTS',
    'MANAGE_SETTINGS',
    'PROCESS_REFUNDS',
    'VIEW_AUDIT_LOGS',
    'MANAGE_PROMOTIONS',
  ],
};

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, name: string) => Promise<void>;
  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  refreshPermissions: () => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL || '';

// Context
const AuthContext = createContext<AuthContextValue | null>(null);

// Provider
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Validate session on mount: show stale data immediately, then verify with server
  useEffect(() => {
    const validateSession = async (): Promise<void> => {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        setIsLoading(false);
        return;
      }

      // Show stale data immediately so UI doesn't flash
      try {
        const parsed = JSON.parse(storedUser);
        if (!parsed.permissions) parsed.permissions = [];
        setUser(parsed);
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        setIsLoading(false);
        return;
      }

      // Validate token against server
      try {
        const response = await apiClient.get('/api/auth/me');
        const freshUser: User = {
          ...response.data.user,
          permissions: response.data.user.permissions || [],
        };
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      } catch (error: unknown) {
        // Only clear on auth errors, not network errors
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          setUser(null);
        }
        // On network error: keep stale data, user stays "logged in"
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Ensure permissions array exists
    const userData: User = {
      ...data.user,
      permissions: data.user.permissions || [],
    };

    // Store token and user
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Registration failed');
    }

    // Auto-login after registration if tokens are returned
    if (data.accessToken) {
      // Ensure permissions array exists
      const userData: User = {
        ...data.user,
        permissions: data.user.permissions || [],
      };

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token || !user) return;

    try {
      const response = await fetch(`${API_URL}/api/auth/me/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const { permissions } = await response.json();
        setUser(prev => prev ? { ...prev, permissions } : null);

        // Update localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          localStorage.setItem('user', JSON.stringify({ ...parsed, permissions }));
        }
      }
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    }
  }, [user]);

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;

      // Prefer server-provided permissions
      if (user.permissions && user.permissions.length > 0) {
        return user.permissions.includes(permission);
      }

      // Fallback to role-based matrix (for users who haven't re-logged in yet)
      const rolePermissions = FALLBACK_PERMISSION_MATRIX[user.role] || [];
      return rolePermissions.includes(permission);
    },
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const isAdmin = useCallback((): boolean => {
    return user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  }, [user]);

  const isSuperAdmin = useCallback((): boolean => {
    return user?.role === 'SUPER_ADMIN';
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        register,
        hasPermission,
        hasAnyPermission,
        isAdmin,
        isSuperAdmin,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Hook
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
